/* usePlanIssues — iter98.75 (Plan Readiness Scorecard, advisory layer).
 *
 * Complements `usePlanCompletion` by surfacing the *granular* issues
 * the strict-mode backend validator would catch — but as advisory
 * nudges, not blockers. Lets the planner keep `STRICT_PLAN_VALIDATION`
 * off during the clinical pilot while still nudging clean data.
 *
 * Detection categories:
 *
 *   1. blocker-when-strict  — would 400 if strict mode flips on:
 *        · Missing tooth_pair on any IPR row
 *        · Invalid FDI tooth code on any per-tooth list
 *        · Duplicate teeth in tooth_movements / attachments / precision_cuts
 *        · IPR row with non-positive stage or non-numeric amount
 *        · Out-of-range numerics on bite metrics / arch crowding / midline
 *
 *   2. clinical-advisory    — soft clinical caution (won't be blocked
 *      even in strict mode but worth flagging):
 *        · IPR per-contact > 0.5mm (clinical max)
 *        · IPR total > 4.0mm (typically requires justification)
 *        · Movement > 1mm mesial/distal with no matching IPR at the
 *          adjacent contact (cross-validation insight)
 *
 * Each issue carries:
 *   { id, severity, label, anchor, sectionId }
 *
 * `anchor` is the DOM id (e.g. "sec-protocol") the scorecard scrolls
 * to. `sectionId` is the editor's section key (e.g. "protocol") so the
 * scorecard can dispatch `plan-section-open` to expand the accordion
 * before scrolling.
 */
import { useMemo } from "react";
import { CLINICAL_REF } from "@/components/case/usePlanCompletion";


/** Minimal FDI tooth set — duplicated from backend for client-side
 *  validation. Keep in sync if the backend list ever changes. */
const FDI_PERMANENT_TEETH = new Set([
    "18", "17", "16", "15", "14", "13", "12", "11",
    "21", "22", "23", "24", "25", "26", "27", "28",
    "48", "47", "46", "45", "44", "43", "42", "41",
    "31", "32", "33", "34", "35", "36", "37", "38",
]);


function isNum(x) { return typeof x === "number" && Number.isFinite(x); }


/** Compute the adjacent-contact pair label for a given movement
 *  direction. Returns null if direction isn't mesial/distal or the
 *  adjacency crosses the midline. */
function expectedIPRPair(toothStr, direction) {
    const t = Number(toothStr);
    if (!Number.isInteger(t)) return null;
    const q = Math.floor(t / 10), p = t % 10;
    if (q < 1 || q > 4 || p < 1 || p > 8) return null;
    let adj = null;
    if (direction === "mesial" && p > 1) adj = q * 10 + (p - 1);
    if (direction === "distal" && p < 8) adj = q * 10 + (p + 1);
    if (!adj) return null;
    const lo = Math.min(t, adj), hi = Math.max(t, adj);
    return `${lo}-${hi}`;
}


export default function usePlanIssues(plan) {
    return useMemo(() => {
        const issues = [];
        const p = plan || {};

        /* === IPR row validation === */
        const iprEntries = Array.isArray(p.ipr_entries) ? p.ipr_entries : [];
        iprEntries.forEach((row, idx) => {
            const tp = (row?.tooth_pair || "").trim();
            if (!tp) {
                issues.push({
                    id: `ipr-${idx}-no-tooth-pair`,
                    severity: "blocker-when-strict",
                    label: `IPR row ${idx + 1} — tooth pair is required`,
                    anchor: "sec-protocol",
                    sectionId: "protocol",
                });
            }
            const amt = Number(row?.amount_mm || 0);
            if (!(amt > 0)) {
                issues.push({
                    id: `ipr-${idx}-bad-amount`,
                    severity: "blocker-when-strict",
                    label: `IPR ${tp || `row ${idx + 1}`} — amount must be > 0mm`,
                    anchor: "sec-protocol",
                    sectionId: "protocol",
                });
            } else if (amt > CLINICAL_REF.ipr_max_per_contact) {
                issues.push({
                    id: `ipr-${idx}-over-cap`,
                    severity: "clinical-advisory",
                    label: `IPR ${tp || `row ${idx + 1}`} — ${amt}mm exceeds the ${CLINICAL_REF.ipr_max_per_contact}mm clinical max`,
                    anchor: "sec-protocol",
                    sectionId: "protocol",
                });
            }
            const stage = Number(row?.stage || 0);
            if (!(Number.isInteger(stage) && stage >= 1)) {
                issues.push({
                    id: `ipr-${idx}-bad-stage`,
                    severity: "blocker-when-strict",
                    label: `IPR ${tp || `row ${idx + 1}`} — stage must be a positive integer`,
                    anchor: "sec-protocol",
                    sectionId: "protocol",
                });
            }
        });
        // IPR total guard
        const iprTotal = iprEntries.reduce((acc, r) => acc + (Number(r.amount_mm) || 0), 0);
        if (iprTotal > CLINICAL_REF.ipr_total_flag) {
            issues.push({
                id: "ipr-total-over-flag",
                severity: "clinical-advisory",
                label: `Total IPR ${iprTotal.toFixed(2)}mm exceeds ${CLINICAL_REF.ipr_total_flag}mm flag · justify in planner notes`,
                anchor: "sec-protocol",
                sectionId: "protocol",
            });
        }

        /* === Per-tooth FDI + duplicate validation === */
        const checkPerToothList = (list, fieldLabel, sectionId, anchor) => {
            const arr = Array.isArray(list) ? list : [];
            const seen = new Map();
            arr.forEach((item, idx) => {
                const tooth = String(item?.tooth || "").trim();
                if (!tooth) return; // empty rows are ignored (UI editor handles dedupe)
                if (!FDI_PERMANENT_TEETH.has(tooth)) {
                    issues.push({
                        id: `${fieldLabel}-${idx}-bad-fdi`,
                        severity: "blocker-when-strict",
                        label: `${fieldLabel} row ${idx + 1} — invalid FDI code "${tooth}"`,
                        anchor,
                        sectionId,
                    });
                }
                if (seen.has(tooth)) {
                    issues.push({
                        id: `${fieldLabel}-${tooth}-dup`,
                        severity: "blocker-when-strict",
                        label: `${fieldLabel} — duplicate entry for tooth ${tooth}`,
                        anchor,
                        sectionId,
                    });
                }
                seen.set(tooth, idx);
            });
        };
        checkPerToothList(p.tooth_movements_plan, "Movements", "pertooth", "sec-pertooth");
        checkPerToothList(p.attachments_plan,     "Attachments", "pertooth", "sec-pertooth");
        checkPerToothList(p.precision_cuts,       "Precision cuts", "pertooth", "sec-pertooth");

        /* === Movement → IPR cross-validation === */
        const iprPairs = new Set(iprEntries.map((r) => (r?.tooth_pair || "").trim()).filter(Boolean));
        const movements = Array.isArray(p.tooth_movements_plan) ? p.tooth_movements_plan : [];
        movements.forEach((mv) => {
            const amt = Number(mv?.amount_mm || 0);
            if (!(amt > 1.0)) return;
            const directions = mv?.directions || [];
            for (const dir of directions) {
                if (dir !== "mesial" && dir !== "distal") continue;
                const expected = expectedIPRPair(mv.tooth, dir);
                if (!expected) continue;
                if (!iprPairs.has(expected)) {
                    issues.push({
                        id: `mv-${mv.tooth}-${dir}-no-ipr`,
                        severity: "clinical-advisory",
                        label: `Tooth ${mv.tooth} moves ${amt.toFixed(1)}mm ${dir} but no IPR at ${expected}`,
                        anchor: "sec-protocol",
                        sectionId: "protocol",
                    });
                }
            }
        });

        /* === Bite metric ranges (only flag when value is set) === */
        const checkRange = (key, value, [lo, hi], unit, label, anchor, sectionId) => {
            if (!isNum(value)) return;
            if (value < lo || value > hi) {
                issues.push({
                    id: `range-${key}`,
                    severity: "blocker-when-strict",
                    label: `${label}: ${value}${unit} outside ${lo}–${hi}${unit}`,
                    anchor,
                    sectionId,
                });
            }
        };
        checkRange("initial_overjet", p.initial_overjet_mm, [-20, 20], "mm", "Initial overjet", "sec-metrics", "bite_metrics");
        checkRange("initial_overbite", p.initial_overbite_mm, [-20, 20], "mm", "Initial overbite", "sec-metrics", "bite_metrics");
        checkRange("final_overjet", p.final_overjet_mm, [-20, 20], "mm", "Final overjet", "sec-metrics", "bite_metrics");
        checkRange("final_overbite", p.final_overbite_mm, [-20, 20], "mm", "Final overbite", "sec-metrics", "bite_metrics");

        /* === Counts === */
        const upper = Number(p.aligner_count_upper || 0);
        const lower = Number(p.aligner_count_lower || 0);
        const arches = p.treatment_arches;
        if (arches === "upper" && lower > 0) {
            issues.push({
                id: "arch-mismatch-lower",
                severity: "clinical-advisory",
                label: `Arches set to "Upper only" but ${lower} lower aligners are still planned`,
                anchor: "sec-wear",
                sectionId: "wear",
            });
        }
        if (arches === "lower" && upper > 0) {
            issues.push({
                id: "arch-mismatch-upper",
                severity: "clinical-advisory",
                label: `Arches set to "Lower only" but ${upper} upper aligners are still planned`,
                anchor: "sec-wear",
                sectionId: "wear",
            });
        }

        const strictBlockers = issues.filter((i) => i.severity === "blocker-when-strict").length;
        const advisories = issues.filter((i) => i.severity === "clinical-advisory").length;
        return { issues, strictBlockers, advisories };
    }, [plan]);
}


/* Test hook: expose adjacency helper for unit tests. */
export const __TEST__ = { expectedIPRPair };
